import { FC, useCallback, useContext, useMemo, useState } from "react";

import { EnumOption, Filter, RefSchemaItem } from "@data-root/types";
import {
  SchemaItemProps,
  useAppContext,
  useClientContext,
  useField,
  useFormulaWithDependencies,
  useRows,
  CustomButton,
} from "@data-root/react";
import {
  Button,
  Skeleton,
  Input,
  Typography,
  Space,
  App as AntdApp,
} from "antd";

import { View } from "@data-root/react";
import { Dialog } from "../../portals/Dialog";
import { CaretDownOutlined } from "@ant-design/icons";
import { Select } from "../../Select";
import { RefInputContext } from ".";
import { getDisplayValueFromRow } from "@data-root/core";
import { Remarks } from "../../remarks";
import { useQuery } from "@tanstack/react-query";
import { ErrorView } from "@data-root/error-boundary";

const { Title } = Typography;

// TODO: Use `useField`
// TODO: Use inline form
// TODO: Support for bidirectional refs
export const RefInputModalPresentation: FC<SchemaItemProps<RefSchemaItem>> = ({
  item,
}) => {
  const antdApp = AntdApp.useApp();
  const { core } = useClientContext();
  const { appSpecifier, styles } = useAppContext();
  const {
    loaded,
    displayName,
    placeholder,
    editable,

    value: _value,
    displayValue: _displayValue,
    setValue: _setValue,
    remarks,
  } = useField<string | string[]>(item);
  const { onAdd, multiple, refTable, pinned } = useContext(RefInputContext)!;

  const [searchFilters, setSearchFilters] = useState<Filter[] | null>(null);

  const onQuery = useCallback(
    (query: string) => {
      if (!query) return setSearchFilters(null);

      setSearchFilters([
        {
          id: refTable.label,
          type: "text",
          value: query,
        },
      ]);
    },
    [refTable.label]
  );

  const { value: inheritedFilters } = useFormulaWithDependencies<Filter[]>(
    core,
    item.filter
  );
  const { value: canAdd } = useFormulaWithDependencies<boolean>(
    core,
    item.canAdd
  );

  const _onAdd = useCallback(() => {
    const canAddItem = item.canAdd === undefined ? true : item.canAdd;

    if (canAddItem || canAdd) {
      onAdd();
    } else {
      antdApp.modal.error({
        title: "Error al Agregar",
        content: "No dispone de permisos para agregar un nuevo registro",
        centered: true,
        okText: "Aceptar",
      });
    }
  }, [item.canAdd, canAdd, onAdd, antdApp.modal]);

  const filters = useMemo(() => {
    const filters = [];

    if (inheritedFilters) filters.push(...inheritedFilters);

    if (searchFilters) filters.push(...searchFilters);

    return filters;
  }, [inheritedFilters, searchFilters]);

  const {
    loaded: refRows_loaded,
    rows: refRows,
    fetch,
    error,
  } = useRows(core, appSpecifier, item.ref, filters);

  const fetchOptions = useCallback(
    async (refRows, refTable) => {
      if (!refRows || !refTable) {
        return [];
      }

      const newOptions = await Promise.all(
        refRows.map(async (row) => {
          const context = {
            appId: appSpecifier.appId,
            deploymentId: appSpecifier.deploymentId,
            tableId: item.ref,
            rowId: row.id,
            row,
          };
          const result = await core.evaluateFormula<string>(
            item.optionsDisplayValue,
            context
          );
          return {
            id: row.id,
            name:
              result ||
              getDisplayValueFromRow(row, { id: refTable.label } as any),
          } as EnumOption;
        })
      );

      return newOptions;
    },
    [
      appSpecifier.appId,
      appSpecifier.deploymentId,
      item.ref,
      item.optionsDisplayValue,
      core,
    ]
  );

  // Fetch selected options if they're not in the current filtered results
  const fetchSelectedOptions = useCallback(
    async (selectedIds: string | string[]) => {
      if (!selectedIds || (Array.isArray(selectedIds) && selectedIds.length === 0)) {
        return [];
      }
      
      const ids = Array.isArray(selectedIds) ? selectedIds : [selectedIds];
      
      try {
        const selectedRows = await Promise.all(
          ids.map(async (id) => {
            const row = await core.getOne(appSpecifier, item.ref, [
              {
                id: "id",
                type: "text",
                value: id,
              },
            ]);
            return row;
          })
        );
        
        const selectedOptions = await Promise.all(
          selectedRows.filter(Boolean).map(async (row) => {
            const context = {
              appId: appSpecifier.appId,
              deploymentId: appSpecifier.deploymentId,
              tableId: item.ref,
              rowId: row.id,
              row,
            };
            const result = await core.evaluateFormula<string>(
              item.optionsDisplayValue,
              context
            );
            return {
              id: row.id,
              name: result || getDisplayValueFromRow(row, { id: refTable.label } as any),
            } as EnumOption;
          })
        );
        
        return selectedOptions;
      } catch (error) {
        console.error("Error fetching selected options:", error);
        return [];
      }
    },
    [appSpecifier, core, item.ref, item.optionsDisplayValue, refTable]
  );

  const {
    data: filteredOptions = [],
    isLoading,
    isError,
    error: options_error,
    isSuccess,
    isFetching,
  } = useQuery({
    queryKey: ["fetchOptions", refRows, refTable],
    queryFn: () => fetchOptions(refRows, refTable),
    enabled: !!refRows && !!refTable,
  });

  const { data: selectedOptions = [] } = useQuery({
    queryKey: ["fetchSelectedOptions", _value, refTable],
    queryFn: () => fetchSelectedOptions(_value),
    enabled: !!_value && !!refTable,
  });

  // Combine filtered options with selected options, removing duplicates
  const options = useMemo(() => {
    if (!selectedOptions.length) return filteredOptions;
    
    const selectedIds = selectedOptions.map(opt => opt.id);
    const filteredWithoutSelected = filteredOptions.filter(
      opt => !selectedIds.includes(opt.id)
    );
    
    return [...selectedOptions, ...filteredWithoutSelected];
  }, [filteredOptions, selectedOptions]);

  const [dialogVisible, setDialogVisible] = useState(false);

  const handleSetValue = useCallback(
    (value: string | string[], displayValue: string) => {
      if (!multiple) setDialogVisible(false);
      // hook_setValue(value);
      // _setDisplayValue(displayValue);
      _setValue(value, displayValue);
    },
    [_setValue, multiple]
  );

  const handleOnOpenDialog = useCallback(() => {
    setDialogVisible(true);
  }, []);

  if (options_error) return <ErrorView error={options_error} />;
  if (error) return <ErrorView error={error} />;

  return (
    <View style={{ width: "100%" }}>
      <Title level={5}>{displayName}</Title>

      {!loaded || !refRows_loaded ? (
        <Skeleton.Input block active />
      ) : (
        <>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
            }}
          >
            <Input
              // key={(item.id + displayValue + value) as string}
              readOnly
              maxLength={10}
              placeholder={placeholder}
              // value={displayValue}
              value={_displayValue}
            />

            <Button
              type="primary"
              icon={<CaretDownOutlined />}
              onClick={handleOnOpenDialog}
              disabled={
                !editable || !refRows || !refTable || !refRows_loaded
                // || (refRows && refRows.length === 0)
              }
            />
          </View>
        </>
      )}

      <Remarks texts={remarks} />
      <Dialog
        title={displayName}
        visible={dialogVisible}
        setVisible={setDialogVisible}
      >
        <Select
          multiple={multiple}
          // value={value}
          value={_value}
          setValue={handleSetValue}
          items={options}
          onQuery={onQuery}
          pinned={pinned}
        />

        <Space>
          <CustomButton
            type="link"
            onClick={fetch}
            style={{
              color: styles.primaryColor,
            }}
          >
            Cargar más
          </CustomButton>

          <Button onClick={_onAdd} type="primary">
            Agregar
          </Button>
        </Space>
      </Dialog>
    </View>
  );
};
